import React, { useState } from 'react';
import styled from 'styled-components';
import Modal from 'components/UI/Modal';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import Select from 'components/UI/Select';
import { Space } from 'styles/themeSpaces';
import { Main, Text } from 'styles/themeColors';
import { TEXT_LABELS } from 'constants/order';

const Label = styled.label`
  margin-bottom: 16px;
  font-size: 14px;
  color: ${Text.primary};
  line-height: 1.5;
`;

const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Space.spHalf};
  width: 100%;
  margin-top: 16px;
`;

const SelectLabel = styled.div`
  font-size: 14px;
  color: ${Text.primary};
  font-weight: 500;
`;

interface Props {
  show: boolean;
  onClose: () => void;
  onConfirm: (selectedOption: { id: string; value: string }) => void;
  title: string;
  dropdownOptions?: Array<{ id: string; value: string }>;
  dropdownLabel?: string;
  dropdownPlaceholder?: string;
  message1?: string;
  message2?: string;
  confirmButtonText?: string;
}

const CancellationApprovalModal = ({
  show,
  onClose,
  onConfirm,
  title,
  dropdownOptions = [],
  dropdownLabel = TEXT_LABELS.SELECT_OPTION_DEFAULT,
  dropdownPlaceholder = TEXT_LABELS.SELECT_OPTION_PLACEHOLDER,
  message1 = TEXT_LABELS.CANCELLATION_APPROVAL_MESSAGE_1,
  message2 = TEXT_LABELS.CANCELLATION_APPROVAL_MESSAGE_2,
  confirmButtonText = TEXT_LABELS.SEND_FOR_APPROVAL
}: Props) => {

  const [selectedReason, setSelectedReason] = useState<{ id: string; value: string }>({ id: '', value: '' });
  const [isDirty, setIsDirty] = useState(false);

  const handleCancelation = () => {
    setSelectedReason({ id: '', value: '' });
    setIsDirty(false);
    onClose();
  };

  const handleConfirmation = () => {
    if (!selectedReason.id && dropdownOptions.length > 0) {
      setIsDirty(true);
      return;
    }

    onConfirm(selectedReason);
    handleCancelation();
  };

  const handleDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = event.target.id;
    const selectedOptionObject = dropdownOptions.find(option => option.id === selectedId);

    setSelectedReason({
      id: selectedId,
      value: selectedOptionObject?.value || event.target.value
    });
    setIsDirty(false);
  };

  return (
    <Modal
      showButtons
      showModal={show}
      title={title}
      ghostButtonText='Cancel'
      primaryButtonText={confirmButtonText}
      onClose={handleCancelation}
      onClick={handleConfirmation}
      theme={"brandDark"}
      borderColor={Main.lightGray}
      color={Main.black}
    >
      <FlexboxContainer flexDirection="column">
        {message1 ? <Label className='base'>
          {message1}
        </Label> : null}
        {message2 ? <Label className='base'>
          {message2}
        </Label> : null}

        {dropdownOptions.length > 0 && (
          <SelectContainer>
            <SelectLabel>{dropdownLabel}</SelectLabel>
            <Select
              value={selectedReason.value}
              onChange={handleDropdownChange}
              options={dropdownOptions}
              width="100%"
              height="36px"
              noBorder={false}
              placeholder={dropdownPlaceholder}
            />
            {isDirty && !selectedReason.id && (
              <div style={{ color: Main.error, fontSize: '12px' }}>
                {TEXT_LABELS.REJECTION_REASON_REQUIRED}
              </div>
            )}
          </SelectContainer>
        )}
      </FlexboxContainer>
    </Modal>
  );
};

export default CancellationApprovalModal;